import { CollectionHead } from "../lib/CollectionHead.tsx";
import { LoadingScreen } from "../lib/Loading.tsx";
import { TileRow } from "../lib/Tiles.tsx";
import { RedeemShareCode } from "../shareCodes/RedeemShareCode.tsx";
import { ActivityList, QuickActions, WorkStrip } from "./DashboardPanels.tsx";
import { DASHBOARD_COPY } from "./dashboard.constants.ts";
import { useDashboardController } from "./useDashboardController.ts";
import "./dashboard.css";

/**
 * The collector's first screen: what the registry holds, sampled four ways.
 *
 * The layout is the prototype's — a row of counts, the collection beside the
 * activity, quick actions under it. What is in the tiles is not: there is no
 * portfolio value, no agent count and no event total, because the registry
 * cannot answer any of the three. See DASHBOARD_COPY.
 */
export function Dashboard({
  registerHref,
  collectionHref,
  workBase,
  viewerOndoId,
}: {
  registerHref: string;
  collectionHref: string;
  /** '/collector/me/work' — where a card in the strip and a work's activity row link to. */
  workBase: string;
  /** Whose dashboard this is, so an act they performed reads "by you". */
  viewerOndoId: string;
}) {
  const { view, chooseCollection } = useDashboardController(workBase, viewerOndoId);

  if (view.kind === "loading") {
    return <LoadingScreen message={DASHBOARD_COPY.loading} />;
  }
  if (view.kind === "error") {
    return (
      <p className="collection__error" role="alert">
        {view.message}
      </p>
    );
  }

  return (
    <section className="dashboard">
      <TileRow tiles={view.tiles} />

      <div className="dashboard__panels">
        <section className="dashboard__panel">
          <CollectionHead
            name={view.collectionName}
            groups={view.collectionGroups}
            selected={view.activeCollection}
            label={DASHBOARD_COPY.collectionLabel}
            link={{ href: collectionHref, label: DASHBOARD_COPY.viewAll }}
            onSelect={chooseCollection}
          />
          {view.emptyMessage ? (
            <p className="collection__note">{view.emptyMessage}</p>
          ) : (
            <WorkStrip cards={view.cards} />
          )}
        </section>

        <div className="dashboard__side">
          {/* Absent rather than empty: a panel headed "Recent Activity" with
              nothing under it reads as a screen that failed to load. It is empty
              when nothing has been recorded yet, and when the feed store is not
              configured for this deployment.

              No "View all" beside it. This IS the whole of the recorded history
              the caller can read — a collection's feed carries what happened to
              the works in it — so there is no fuller list to open. */}
          {view.activity.length > 0 && (
            <section className="dashboard__panel">
              <h2 className="panel-head__title panel-head__title--alone">
                {DASHBOARD_COPY.recentActivity}
              </h2>
              <ActivityList rows={view.activity} />
            </section>
          )}

          <QuickActions registerHref={registerHref} collectionHref={collectionHref} />

          {/* The door in for a share code — see 20260910123000. On the landing
              screen because that is where somebody arrives holding one. */}
          <RedeemShareCode workBase={workBase} />
        </div>
      </div>
    </section>
  );
}
